/**
 * Grid Layout Tokens
 * Responsive grid patterns for different content types
 */

export const GRIDS = {
  // Metric cards
  metricsDuo: "grid grid-cols-1 sm:grid-cols-2 gap-4",
  metricsQuad: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4",
  metricsQuint: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4",
  metricsTrio: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4",
  metricsSix: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4",
  metricsQuintResponsive: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4",
  metricsQuintMobile: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3",
  metricsQuadMobile: "grid grid-cols-2 sm:grid-cols-4 gap-4",
  metricsQuadMobileGap3: "grid grid-cols-2 sm:grid-cols-4 gap-3",
  metricsQuadMobileCompact: "grid grid-cols-2 sm:grid-cols-4 gap-2",
  metricsTrioMobileCompact: "grid grid-cols-3 gap-2",
  metricsQuintMobileCompact: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2",
  metricsQuadResponsive: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4",
  metricsTrioFixed: "grid grid-cols-3 gap-4",
  
  // ─────────────────────────────────────────────────────────────────
  // FEATURED CONTENT GRIDS - Cards for experts, publications, etc.
  // ─────────────────────────────────────────────────────────────────
  
  /** Featured content: 1 col mobile → 2 col tablet → 3 col desktop (gap-4) */
  featuredGrid: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  
  /** Featured content with larger gaps (gap-6) */
  featuredGridSpaced: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
  
  // Content cards
  contentDual: "grid grid-cols-1 lg:grid-cols-2 gap-6",
  contentTriple: "grid grid-cols-1 lg:grid-cols-3 gap-4",
  /** @deprecated Use GRIDS.featuredGrid instead */
  contentTripleMd: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  
  // User/Expert card grids (responsive 1→2→3 columns)
  /** @deprecated Use GRIDS.featuredGrid instead */
  userCardGrid: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  /** @deprecated Use GRIDS.featuredGridSpaced instead */
  contentTripleMdSpaced: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
  
  // Service/status grids
  serviceGrid: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3",
  
  // Form layouts
  formGrid: "grid grid-cols-1 md:grid-cols-2 gap-4",
  formGridSpaced: "grid grid-cols-1 md:grid-cols-2 gap-6",
  formGridDual: "grid grid-cols-2 gap-4",
  
  // Filter grids
  filterTrio: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4",
  
  // Action grids
  actionGrid3: "grid grid-cols-1 md:grid-cols-3 gap-4",
  formGrid4: "grid grid-cols-1 md:grid-cols-4 gap-4",
  
  // Stats grids (responsive 5-column)
  statsQuintSkeleton: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4",
  
  // Form grids with sm breakpoint
  formGridSm: "grid grid-cols-1 sm:grid-cols-2 gap-4",
  formGridSmSpaced: "grid grid-cols-1 sm:grid-cols-2 gap-6",
  formGridSmResponsive: "grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4",
  
  // Content grids with sm+lg breakpoints
  contentTripleSm: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
  contentTripleSmSpaced: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6",
  contentQuadSm: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4",
  
  // Responsive trio from sm breakpoint (for home page, overview grids)
  contentTrioSm: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4",
  
  // Responsive quint from sm breakpoint with lg:5 (for quick actions)
  contentQuintSm: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3",
  
  // 3-column form grids at sm breakpoint
  formGridSmTrio: "grid grid-cols-1 sm:grid-cols-3 gap-4",
  formGridSmTrioCompact: "grid grid-cols-1 sm:grid-cols-3 gap-3",
  formGridSmTrioGap2: "grid grid-cols-1 sm:grid-cols-3 gap-2",
  
  // ─────────────────────────────────────────────────────────────────
  // VIEW FILTER GRIDS - For interactive view filter cards
  // ─────────────────────────────────────────────────────────────────
  
  /** 6 view cards: 2 cols mobile, 3 cols tablet, 6 cols desktop - equal height */
  viewFiltersSix: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-stretch",
  
  /** 5 view cards: 2 cols mobile, 3 cols tablet, 5 cols desktop */
  viewFiltersFive: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3",
  
  // ─────────────────────────────────────────────────────────────────
  // WRAP PATTERNS - Flex-wrap layouts for tags, badges, pills
  // ─────────────────────────────────────────────────────────────────
  
  /** Standard tag/badge wrap (gap-2) */
  tagWrap: "flex flex-wrap gap-2",
  
  /** Tag wrap with items centered */
  tagWrapCenter: "flex flex-wrap gap-2 items-center",
  
  /** Pill button row with bottom margin */
  pillRow: "flex flex-wrap gap-2 mb-8",
  
  /** Filter/control wrap (gap-3) */
  controlWrap: "flex flex-wrap gap-3",
  
  /** Control wrap with centered items */
  controlWrapCenter: "flex flex-wrap gap-3 items-center",
} as const;

export type GridToken = keyof typeof GRIDS;
